/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { EndpointUrlParams } from 'src/core/utils/endpointBuilder/types';
import { getEventBus } from 'src/core/utils/eventBus';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { getSingleTestService } from 'src/luis/projects/testPane/services/singleTestService';
import { EndpointPredictionSwagger } from 'src/luis/projects/testPane/types/swagger/EndpointPredictionSwagger';
import { UtteranceResultStore } from 'src/luis/projects/testPane/types/types';
import { LuisId } from 'src/luis/types/Resource';
import { TestPanePredictionStore } from 'src/luis/types/stores/testPane/TestPanePredictionStore';

export type SingleTestHandlerGroup = ReturnType<typeof createSingleTestHandlerGroup>;

/**
 * Represents a handler group for single test operations.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The handler group dependencies.
 */
export const createSingleTestHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const eventBus = getEventBus();
    const logger = getLogger();

    eventBus.on('notifyTrainingSucceeded', ({ appId }) => {
        const currentAppStore = rootStore.currentLuisAppStore;

        if (currentAppStore?.id === appId) {
            currentAppStore.currentAppVersionStore?.testPanePredictionStoreHouse.setItems([]);
        }
    });

    /**
     * Gets the test pane prediction store house for
     * the given application and version id.
     *
     * @param appId The application id of the prediction store house to get.
     * @param versionId The version if of the prediction store house to get.
     * @returns The prediction store house for the test pane.
     */
    const getPredictionStoreHouse = (appId: LuisId, versionId: string) =>
        rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId).testPanePredictionStoreHouse;

    /**
     * Predict the current utterance.
     *
     * @param param0.text The text of the utterance.
     * @param param0.entities The text of the utterance.
     */
    const predictTestUtterance = async ({ text, entities, context }: HandlerParams<{ text: string; entities: Entity[] }>) => {
        const { appId, versionId } = context;
        const storeHouse = getPredictionStoreHouse(appId, versionId);

        const logTimedActivity = logger.createTimedActivityLogger<TestPanePredictionStore>('PredictTestUtterance');

        storeHouse.setIsLoading(true);

        const prediction = await logTimedActivity(getSingleTestService({ context }).predict({ text, entities }));

        storeHouse.add(prediction);
        storeHouse.setIsLoading(false);
    };

    /**
     * Fires the endpoint with the given parameters.
     *
     * @param param0.params The url parameters that will be used
     * to build the endpoint url.
     */
    const predictEndpointTestUtterance = async ({ params, tracker, context }: HandlerParams<{ params: EndpointUrlParams }>) => {
        const logTimedActivity = logger.createTimedActivityLogger<[UtteranceResultStore, EndpointPredictionSwagger]>(
            'PredictEndpointTestUtterance',
            () => ({ query: params.query })
        );

        const data = await logTimedActivity(getSingleTestService({ context }).predictEndpoint({ params }));

        if (tracker) {
            tracker.setContext(data);
        }
    };

    /**
     * Clears the test pane utterances in the prediction store
     * of the given application and version id.
     */
    const clearTestUtterances = async (params?: HandlerParams<object>) => {
        const { appId, versionId } = params.context;
        const storeHouse = getPredictionStoreHouse(appId, versionId);

        storeHouse.setItems([]);
    };

    return { predictTestUtterance, predictEndpointTestUtterance, clearTestUtterances };
};
